import {useEffect, useState} from 'react';
import {useCategoriesWithTags} from '../../common/hooks/useCategories';
import {SearchInput} from '../search/SearchInput';
import {
  mapGetCategoriesResponseToScreenInterests,
  mapTagCodesToScreenInterests,
} from '../../utils/mappers';
import {CategoryWithTags} from '../tags/CategoryWithTags';
import {DashButton} from '../button/DashButton';
import {useTagsStore} from '../../common/stores/useTagsStore';
import {useLocation, useNavigate} from 'react-router-dom';
import {useUiStore} from '../../common/stores/useUiStore';
import routes from '../routes/routes';

export interface ScreenInterest {
  tagName: string;
  tagCode: number;
  categoryName: string;
  categoryCode: number;
}

export const TagSelectionScreen = () => {
  const [filterSearchText, setFilterSearchText] = useState('');
  const {data: categoriesResponse, isLoading} = useCategoriesWithTags();
  const {setSelectedTagCodes, selectedTagCodes} = useTagsStore();
  const {setShowBackButton, setIsLoading} = useUiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = location.state?.returnTo;

  const [filteredInterests, setFilteredInterests] = useState<ScreenInterest[]>(
    [],
  );
  const [allInterests, setAllInterests] = useState<ScreenInterest[]>([]);

  const [selectedInterests, setSelectedInterests] = useState<ScreenInterest[]>(
    [],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowBackButton(true);
    return () => {
      setShowBackButton(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoading(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (selectedTagCodes && categoriesResponse) {
      const screenInterests = mapTagCodesToScreenInterests(
        selectedTagCodes,
        categoriesResponse || [],
      );
      setSelectedInterests(screenInterests);
    }
  }, [selectedTagCodes, categoriesResponse]);

  useEffect(() => {
    if (categoriesResponse) {
      const mappedAllInterests =
        mapGetCategoriesResponseToScreenInterests(categoriesResponse);
      setAllInterests(mappedAllInterests);
    }
  }, [categoriesResponse]);

  useEffect(() => {
    const searchText = filterSearchText.toLowerCase() || '';

    if (searchText === '') {
      setFilteredInterests(allInterests);
    } else {
      setFilteredInterests(
        allInterests.filter(
          interest =>
            interest.tagName.toLowerCase().includes(searchText) ||
            interest.categoryName.toLowerCase().includes(searchText),
        ),
      );
    }
  }, [filterSearchText, allInterests]);

  const handleToggleInterests = (interest: ScreenInterest) => {
    // setIsPristine(false);
    setSelectedInterests(prev =>
      prev.some(item => item.tagName === interest.tagName)
        ? prev.filter(item => item.tagName !== interest.tagName)
        : [...prev, interest],
    );
  };

  const handleOnSearch = (tagName: string) => {
    setFilterSearchText(tagName.toLowerCase());
  };

  const handleOnApplyClick = () => {
    setSelectedTagCodes(selectedInterests.map(item => item.tagCode));
    navigate(returnPath, {
      replace: true,
      state: {
        from: routes.tagSelection,
        scrollPosition: location.state?.scrollPosition,
        initialScrollPosition: location.state?.scrollPosition,
      },
    });
  };

  const groupedInterestsByCategory = filteredInterests.reduce(
    (acc, interest) => {
      if (!acc[interest.categoryName]) {
        acc[interest.categoryName] = [];
      }
      acc[interest.categoryName]?.push(interest);
      return acc;
    },
    {} as Record<string, ScreenInterest[] | undefined>,
  );

  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center', // This centers the search bar and content
        padding: '20px 20px 100px 20px',
        marginLeft: 50,
        marginRight: 50,
        marginTop: 20,
      }}>
      <h2 style={{color: '#fff', marginBottom: '30px', textAlign: 'center'}}>
        Select Tags
      </h2>
      <div style={{marginBottom: 50, width: '100%', maxWidth: 600}}>
        <SearchInput onSearch={handleOnSearch} debounceMs={0} />
      </div>
      <div>
        {filteredInterests.length &&
          categoriesResponse?.map(({name}) => (
            <CategoryWithTags
              categoryInterests={groupedInterestsByCategory[name] || []}
              categoryName={name}
              selectedInterests={selectedInterests}
              toggleInterest={handleToggleInterests}
              key={name}
            />
          ))}
      </div>
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(26, 26, 26, 0.9)', // Semi-transparent dark bg
          padding: '20px',
          display: 'flex',
          justifyContent: 'center',
          borderTop: '1px solid #333',
          backdropFilter: 'blur(1px)', // Nice glass effect for modern web
          zIndex: 1000,
        }}>
        <div style={{width: 300}}>
          <DashButton text="Apply Selected Tags" onClick={handleOnApplyClick} />
        </div>
      </div>
    </div>
  );
};
